// ERSB Map
// web services

// 03/2025
// 06/2025 - global library updates
//  04/2026 - global ui updates

// Naming conventions used in this code:
// variables that begin with $ contain jquery elements or selectors
// functions that begin with $ return a single jquery element

import {setOptions, importLibrary} from "@googlemaps/js-api-loader";
import $ from "jquery";
import "jquery-ui/dist/jquery-ui.min";
import {
    $targets,
    chicago,
    chicagoBounds,
    geocodeRequest,
    getAPIValues,
    googleAPIKey,
    initModal,
    labelPosition,
    menuHighlight,
    positionMarkersOnMap,
    setAddressMarker,
    setMapBounds,
    showPolygon,
    updateCMSElements,
    POLYGON_STYLE,
    $infoRow,
} from "../../../global/map-library"
import "../../../global/map-library.scss"
import "../scss/styles.scss";

// add target elements specific to this map
$targets.displayAll = $("#check-display-all");

const urlParams = new URLSearchParams(window.location.search);
const fullscreen = urlParams.get("fullscreen");

// add selection highlighting to jquery ui autocomplete menu
$.ui.autocomplete.prototype._renderItem = menuHighlight;

setOptions({
    key: googleAPIKey("AIzaSyDpOvyEN-UDIWKQSJvgqOw0OKOy5JJ5Cvg"), // ERSB Map key - this should be unique for every project
    v: "weekly",
});

try {
    ///////////////////////////////////// GOOGLE MAP LIBRARIES /////////////////////////////////////

    const {Map, InfoWindow, Polygon} = await importLibrary("maps");
    const {AutocompleteSessionToken, AutocompleteSuggestion} = await importLibrary("places");
    await importLibrary("marker");
    await importLibrary("geometry");

    ///////////////////////////////////// GLOBALS /////////////////////////////////////
    const ersbAPI = "/api/ersbgeojson/lists";
    const labelMarkers = [];
    const infoWindow = new InfoWindow();
    let addressMarker;
    let loaded = false; // used to determine initial map load and prevent looping
    // let selectedFeatureName;
    let selectedFeature;

    /*************************************************************************************************************/

    // Read all data
    const [data] = await getAPIValues(ersbAPI);

    // Update the page with contents from the CMS
    updateCMSElements($("h1", $targets.overlay), window.appConfig.pageHeading, $($targets.bodyText), window.appConfig.pageContent);

    // create main map object
    const map = new Map($targets.map[0], {
        mapId: "93be0a261f2638c6", //ERSB KEY
        center: chicago,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: true,
        mapTypeControlOptions: {
            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: google.maps.ControlPosition.TOP_RIGHT
        }
    });

    // show UI
    if (fullscreen) {
        $targets.mainWrapper.toggle(false);
        $targets.displayToggle.attr("aria-expanded", (index, attr) => (attr === "true" ? "false" : "true"));
    }
    $targets.dataLoader.remove();
    $targets.overlay.removeAttr("hidden");

    setMapBounds(map);

    // Create a session token for autocomplete suggestions
    const token = new AutocompleteSessionToken();

    // create an autocomplete menu based on the incoming data
    $targets.addressInput.autocomplete({
        source: async function (request, response) {
            // create the list of addresses with google's Places(new) autocomplete with a shortened address as the label and "address" as the value

            let addressList = [];
            if (request.term.length > 1) {
                const autocompleteRequest = {
                    input: request.term,
                    locationRestriction: chicagoBounds,
                    origin: chicago,
                    includedPrimaryTypes: ["street_address"],
                    language: "en-US",
                    region: "us",
                    sessionToken: token
                };
                const {suggestions} = await AutocompleteSuggestion.fetchAutocompleteSuggestions(autocompleteRequest);
                for (const suggestion of suggestions) {
                    if (suggestion.placePrediction.text.toString().endsWith("Chicago, IL")) {
                        addressList.push({
                            label: suggestion.placePrediction.mainText.toString(),
                            value: "address"
                        });
                    }
                }
            }

            // return the list for the autocomplete menu as the response
            response(addressList);
        },
        focus: function (event, ui) { // autocomplete result is focused on
            $targets.addressInput.val(ui.item.label);
            return false;
        },
        select: (e, ui) => {
            e.preventDefault();
            $targets.addressInput.val(ui.item.label);
            $targets.addressInput.autocomplete("close");
            geocodeRequest(ui.item.label, googleGeoCallback);
        }
    });

    /********************* EVENTS *********************/

    // pan the map into the proper position after everything is drawn
    map.addListener("tilesloaded", () => {
        if (!loaded) {
            setTimeout(() => {
                // fires more reliably when wrapped in an asynchronous call. Using a delay 0 in this case
                positionMarkersOnMap(map, $targets);
                loaded = true;
            }, 0);
        }
    });

    // resize the labels after the map is done with any updates
    google.maps.event.addListener(map, "idle", function () {
        $(".map-label").css({"font-size": map.getZoom() >= 12 ? "24px" : "14px"});
    });

    // find me button - do a geocode request with the user's browser location
    $targets.findMe.on("click", (e) => {
        //check to see if http or https (only works with https)
        if (top.location.href.split(":")[0] !== "https") {
            const $modal = initModal("Secure Connection Required", $("<p>").html("This feature requires a secure connection."));
            const myModal = new Modal($modal[0]);
            myModal.show();
            return false;
        }

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(position => {
                geocodeRequest(new google.maps.LatLng(position.coords.latitude, position.coords.longitude), googleGeoCallback);
            }, err => {
                console.warn(`ERROR(${err.code}): ${err.message}`);
                const $modal = initModal("Browser Location Required", $("<p>").html("This feature requires location to be enabled in your browser and OS."));
                const myModal = new Modal($modal);
                myModal.show();
                return false;
            }, {timeout: 4000});
        }
    });

    // enter key - do geocode request on input field
    $targets.addressInput.on("keydown", async (e) => {
        if (e.key === "Enter") { // Check if the Enter key is pressed
            if ($targets.addressInput.autocomplete) {
                $targets.addressInput.autocomplete("close");
            }
            geocodeRequest(e.target.value, googleGeoCallback);
        }
    });

    // reset button
    $targets.reset.on("click", resetMap);

    // display all regions checkbox
    $targets.displayAll.on("click", (event) => {
        if (event.target.checked) {
            showAllPolygons();
        } else {
            showSelectedPolygon();
        }
    });

    // keep track of the selected feature. On closing the window set the selected feature back to the address pin if it exists.
    infoWindow.addListener("closeclick", () => {
        if (addressMarker) {
            selectedFeature = addressMarker.feature;
        } else {
            selectedFeature = null;
        }
    });

    // collapse main window
    $targets.displayToggle.on("click", (e) => {
        $targets.mainWrapper.toggle(1000);
        $targets.displayToggle.attr("aria-expanded", (index, attr) => (attr === "true" ? "false" : "true"));
        autoSetMapBounds();
        positionMarkersOnMap(map, $targets);
    });

    // scale the map and move the markers if the window size changes
    $(window).on("resize", () => {
        autoSetMapBounds();
        positionMarkersOnMap(map, $targets);
    });

    //hold mouse down for 1.5 seconds to trigger radius search
    new LongPress(1500);
    map.addListener("longPress", event => geocodeRequest(event.latLng, googleGeoCallback));

    // find the feature polygon that contains the given location and display it on the map
    // also handle the infowindow and map scaling here
    function findAndShowPolygon(results) {
        const location = {lat: results.geometry.location.lat(), lng: results.geometry.location.lng()}
        const address = results.formatted_address.replace(/, USA$/, "");  // remove ", USA" from returned address

        // set UI to proper state
        clearMapElements();
        $targets.displayAll.prop("checked", false);

        // show the address marker and pan to it in case it is out of bounds and we stop here
        if (addressMarker) {
            addressMarker.map = null;
        }
        addressMarker = setAddressMarker(map, location, address);
        map.panTo(location);

        // see if the location is contained within any of the feature polygons
        const foundFeature = intersectingFeature(location);
        if (foundFeature) {
            selectedFeature = foundFeature;

            // store the selected polygon's info in the addressmarker for the infowindow
            addressMarker.feature = foundFeature;
            addressMarker.title = address;

            const polygon = showPolygon(map, foundFeature, POLYGON_STYLE(false, false), foundFeature.properties.Name.replace("Sub District", "").trim());
            labelMarkers.push(polygon.labelMarker);
            openInfoWindow(foundFeature);

            setMapBounds(map, foundFeature);

            positionMarkersOnMap(map, $targets);
        } else {
            const $modal = initModal("Out of Bounds", $("<p>").html("Address is out of bounds"));
            const myModal = new Modal($modal);
            myModal.show();
        }

        // look through the dataset and return the feature polygon that contains the location
        function intersectingFeature(location) {
            let found;
            data.features.forEach(f => {
                if (f.geometry.type === "Polygon") {
                    if (checkPolygon(location, f.geometry.coordinates)) {
                        found = f;
                    }
                } else if (f.geometry.type === "MultiPolygon") {
                    f.geometry.coordinates.forEach(m => {
                        if (checkPolygon(location, m)) {
                            found = f;
                        }
                    });
                }
            });

            return found;

            function checkPolygon(location, coordinates) {
                // incoming coordinates are an array of arrays of [longitude, latitude] pairs. Convert them to an array of arrays google LatLng objects
                // if the top level array has one list, it is a simple polygon. Additional lists of coordinates are holes in the main polygon
                // we are creating a maps.Polygon from coordinates to check against rather than a map.data.addGeoJson object from the feature in order to use maps.geometry and containsLocation
                const latLngCoordinates = [];
                coordinates.forEach(p => {
                    const pList = [];
                    p.forEach(c => pList.push(new google.maps.LatLng(c[1], c[0])));
                    latLngCoordinates.push(pList);
                });

                // create a map polygon with the latLngCoordinates to check against the location
                const testPolygon = new Polygon({paths: latLngCoordinates});
                const test = google.maps.geometry.poly.containsLocation(location, testPolygon);
                testPolygon.setMap(null);
                return test;
            }
        }
    }

    // handle logic for different types of scaling specific to this map
    function autoSetMapBounds() {
        if ($targets.displayAll.prop("checked")) {
            setMapBounds(map, data);
        } else if (selectedFeature) {
            setMapBounds(map, selectedFeature, addressMarker);
        } else {
            setMapBounds(map);
        }
    }

    function googleGeoCallback(results, status, request) {
        $targets.addressInput.trigger("blur");
        // geocoder status ok
        if (status === google.maps.GeocoderStatus.OK) {
            if (results[0]) {
                $targets.addressInput.val(results[0].formatted_address.replace(/, USA$/, ""));  // remove ", USA" from returned address
                findAndShowPolygon(results[0]);
            }
        }

        // geocoder status not ok
        else {
            if (request.location) // if we did a lat/long search
                alert(`Geocoder failed due to: ${status}`);
            else // if we did an address search
                alert(`We could not find your address: ${status}`);
        }
    }

    // handles logic for what type of polygon(s) to draw
    function showSelectedPolygon() {
        infoWindow.close();
        clearMapElements();

        if (selectedFeature) {
            // const feature = data.features.find(f => f.properties.Name === selectedFeatureName);
            const polygon = showPolygon(map, selectedFeature, POLYGON_STYLE(false, false), noDistrict(selectedFeature.properties.Name));
            labelMarkers.push(polygon.labelMarker);
            openInfoWindow(selectedFeature);

            setMapBounds(map, selectedFeature, addressMarker);
            positionMarkersOnMap(map, $targets);
        }
    }

    // add all region polygons to the map with rollover and click events
    function showAllPolygons() {
        if (addressMarker) {
            selectedFeature = addressMarker.feature;
        } else {
            selectedFeature = null;
        }

        clearMapElements();

        // set the polygons to be clickable
        // map.data.setStyle(POLYGON_STYLE(true, false));

        // Show marker on region click
        map.data.addListener("click", async (event) => {
            const name = event.feature.getProperty("Name");
            selectedFeature = data.features.find(f => f.properties.Name === name);
            openInfoWindow(selectedFeature);
        });

        // change the polygon style on hover
        map.data.addListener("mouseover", (event) => map.data.overrideStyle(event.feature, POLYGON_STYLE(true, true)));
        map.data.addListener("mouseout", (event) => map.data.overrideStyle(event.feature, POLYGON_STYLE(true, false)));

        // draw the polygons
        data.features.forEach(f => {
            const polygon = showPolygon(map, f, POLYGON_STYLE(true, false), noDistrict(f.properties.Name));
            labelMarkers.push(polygon.labelMarker);
        });
        setMapBounds(map, data);
        positionMarkersOnMap(map, $targets);
    }

    // populates the info window
    function openInfoWindow(feature) {
        const properties = feature.properties;

        // const position = properties.labelPosition;
        const position = labelPosition(feature); // calculate label position for infowindow

        // have the info window appear above the text label
        infoWindow.setOptions({
            pixelOffset: new google.maps.Size(0, -16),
            maxWidth: 600,
        });
        const header = infoWindowHeaderContent(properties);
        const content = infoWindowContent(properties);
        infoWindow.setHeaderContent(header);
        infoWindow.setContent(content);
        infoWindow.setPosition(position);
        infoWindow.open(map);
        infoWindow.addListener("visible", () => {
            $(".gm-style-iw-chr button[title=Close]").trigger("blur");
        }); // blur the selection rectangle around the close X

        function infoWindowHeaderContent(p) {
            const $content = $("<div>").addClass("infowindow-content").html($("<h4>").html(p.Name));
            return $content[0];
        }

        function infoWindowContent(p) {
            const $content = $("<div>").addClass("infowindow-content");
            const $infoTable = $("<table>").addClass("table table-sm table-borderless mb-0");
            const $tbody = $("<tbody>").html($infoRow("School Board Member: ", p.SCHOOL_BOARD_MEMBER));
            $content.html($infoTable.html($tbody));
            return $content[0];
        }
    }

    // reset btn
    function resetMap() {
        infoWindow.close();
        clearMapElements(); //clear polygons and labels

        if (addressMarker) {
            addressMarker.setMap(null);
            addressMarker = null;
        }

        // reset address
        $targets.addressInput.val("");

        selectedFeature = null;

        //reset filters
        $targets.displayAll.prop("checked", false);

        // reposition map
        setMapBounds(map);
        positionMarkersOnMap(map, $targets);
    }

    /******************** MAP FUNCTIONS ********************/

    // clear out elements displayed on the map
    function clearMapElements() {
        // remove polygons
        map.data.forEach(feature => map.data.remove(feature));

        // remove labels
        labelMarkers.forEach(l => l.map = null);
        labelMarkers.length = 0;
    }

    // LongPress on the map sets up mouse events and custom longPress event
    // thanks to stackoverflow - Leiko
    // https://stackoverflow.com/questions/6264853/using-longclick-taphold-with-google-maps-in-jquery-mobile
    function LongPress(length) {
        this.length_ = length;
        this.map_ = map;
        this.timeoutId_ = null;
        map.addListener("mousedown", e => this.onMouseDown_(e));
        map.addListener("mouseup", e => this.onMouseUp_(e));
        map.addListener("drag", e => this.onMapDrag_(e));
    }

    LongPress.prototype.onMouseUp_ = function (e) {
        clearTimeout(this.timeoutId_);
    };

    LongPress.prototype.onMouseDown_ = function (e) {
        clearTimeout(this.timeoutId_);
        const map = this.map_;
        this.timeoutId_ = setTimeout(() => google.maps.event.trigger(map, "longPress", e), this.length_);
    };

    LongPress.prototype.onMapDrag_ = function (e) {
        clearTimeout(this.timeoutId_);
    };

    function noDistrict(s) {
        return s.replace("Sub District", "").trim();
    }

} catch (e) {
    console.error(e);
}
